import { Box, Typography, CircularProgress, FormControl, InputLabel, Select, MenuItem } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { useEffect, useMemo, useRef, useState } from "react"
import { fetchRoadmapByPeriod, FetchRoadmapResponse, NewsQuery } from "../newsApi"
import { useApiQuery } from "../../../core/api/useApiQuery"
import { RoadmapImplementationType, RoadmapItemType } from "../newsTypes"
import { RoadmapFeatureCard } from "./RoadmapFeatureCard"
import { RoadmapTimelineItem } from "./RoadmapTimelineItem"
import { Timeline, timelineItemClasses } from "@mui/lab"
import { LocalizedStrict } from "../../localization/components/AppLocalized"
import { useActiveScrollItem } from "../useActiveScrollItem"
import { LoadingSpinner } from "../../../shared/components/loading/LoadingSpinner"

const useStyles = makeStyles()((theme) => ({
    root: {
        padding: "0 1rem",
    },
    layout: {
        display: "grid",
        gridTemplateColumns: "2fr 1fr",
        gap: "1.5rem",
        alignItems: "start",
    },
    mainContent: {
        order: 1,
        overflowY: "auto",
        paddingBottom: "35rem",
        height: "100%",
    },
    timeline: {
        order: 2,
        height: "100%",
        overflowY: "auto",
        position: "sticky",
        top: 0,
    },
    error: {
        color: theme.palette.error.main,
        textAlign: "center",
        padding: theme.spacing(4),
    },
    timelineContainer: {
        position: "sticky",
        top: "4rem",
        order: 2,
        overflowY: "auto",
    },
    selectWrap: {
        backgroundColor: "white",
        marginBottom: "1.5rem",
    },
}))

type SelectType = RoadmapItemType | RoadmapImplementationType | "All"

const selectNews = (data: FetchRoadmapResponse) => data.roadmaps

export function RoadmapCardTab() {
    const { classes } = useStyles()
    const { data, isLoading, error } = useApiQuery(fetchRoadmapByPeriod, {
        queryName: NewsQuery.LIST,
        dependencies: {},
        select: selectNews,
    })
    const featureRefs = useRef<Map<string, HTMLDivElement>>(new Map())
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const [roadmapType, setRoadmapType] = useState<SelectType>("All")

    const features = useMemo(() => {
        if (!data) return []
        if (roadmapType === "All") return data

        return data.filter((item) => {
            if (roadmapType === RoadmapItemType.UPCOMING)
                return item.roadmapDetails.itemType === RoadmapItemType.UPCOMING
            if (roadmapType === RoadmapImplementationType.BUGFIX) {
                return (
                    item.roadmapDetails.itemType === RoadmapItemType.IMPLEMENTED &&
                    item.roadmapDetails.implementationType === RoadmapImplementationType.BUGFIX
                )
            }
            if (roadmapType === RoadmapImplementationType.FEATURE) {
                return (
                    item.roadmapDetails.itemType === RoadmapItemType.IMPLEMENTED &&
                    item.roadmapDetails.implementationType === RoadmapImplementationType.FEATURE
                )
            }
            return false
        })
    }, [data, roadmapType])
    const { activeItemId, setActiveItemId } = useActiveScrollItem(featureRefs, features)

    useEffect(() => {
        if (features && features.length > 0 && !activeItemId) {
            setActiveItemId(features[0].permaLink)
        }
    }, [features, activeItemId])

    const handleTimelineClick = (permaLink: string) => {
        const element = featureRefs.current.get(permaLink)
        if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" })
        }
    }

    if (isLoading) {
        return (
            <Box className={classes.root}>
                <LoadingSpinner />
            </Box>
        )
    }

    if (error) {
        return (
            <Box className={classes.root}>
                <LocalizedStrict id="news-new-load-error">
                    <Typography className={classes.error}>Error loading roadmap features</Typography>
                </LocalizedStrict>
            </Box>
        )
    }

    return (
        <Box className={classes.root}>
            <FormControl sx={{ width: "10rem" }}>
                <LocalizedStrict id="news-type">
                    <InputLabel>Type</InputLabel>
                </LocalizedStrict>
                <Select
                    onChange={(e) => {
                        setRoadmapType(e.target.value as RoadmapItemType | RoadmapImplementationType)
                    }}
                    value={roadmapType}
                    className={classes.selectWrap}
                >
                    <MenuItem value={"All"}>
                        <LocalizedStrict id="news-all">
                            <span>All</span>
                        </LocalizedStrict>
                    </MenuItem>
                    <MenuItem value={RoadmapImplementationType.BUGFIX}>
                        <LocalizedStrict id="news-bug-fix">
                            <span>Bug fix</span>
                        </LocalizedStrict>
                    </MenuItem>
                    <MenuItem value={RoadmapImplementationType.FEATURE}>
                        <LocalizedStrict id="news-new-feature">
                            <span>New feature</span>
                        </LocalizedStrict>
                    </MenuItem>
                    <MenuItem value={RoadmapItemType.UPCOMING}>
                        <LocalizedStrict id="news-upcoming">
                            <span>Upcoming</span>
                        </LocalizedStrict>
                    </MenuItem>
                </Select>
            </FormControl>
            <Box className={classes.layout}>
                <Box className={classes.mainContent} ref={scrollContainerRef}>
                    {features?.map((item) => (
                        <RoadmapFeatureCard
                            key={item.permaLink}
                            item={item}
                            ref={(el) => {
                                if (el) {
                                    featureRefs.current.set(item.permaLink, el)
                                } else {
                                    featureRefs.current.delete(item.permaLink)
                                }
                            }}
                        />
                    ))}
                </Box>

                <Box className={classes.timelineContainer}>
                    <Timeline
                        className={classes.timeline}
                        position="right"
                        sx={{
                            position: "relative",
                            top: "-1rem",
                            [`& .${timelineItemClasses.root}:before`]: {
                                flex: 0,
                                padding: 0,
                            },
                        }}
                    >
                        {features?.map((item, index) => (
                            <RoadmapTimelineItem
                                key={item.permaLink}
                                item={item}
                                isLast={index === features.length - 1}
                                isActive={activeItemId === item.permaLink}
                                onClick={() => handleTimelineClick(item.permaLink)}
                            />
                        ))}
                    </Timeline>
                </Box>
            </Box>
        </Box>
    )
}
